import { useCallback, useEffect, useState } from 'react';
import { getCredits } from './creditsApi';

export const CREDITS_EVENT = 'withme:credits';

// A read newer than this is not repeated when another part of the page asks.
const FRESH_MS = 60 * 1000;

// The newest payload this page has seen, whichever response carried it. Kept
// outside React so a chip that mounts after the answer arrived still starts
// from it, and so the credits are fetched once per page, not once per chip.
let latest = null;
let updatedAt = 0;
let inflight = null;

// What the dashboard says about this creator (influencers/views.py, next to ai_team_active): whether credits are on
// for her (`agent_credits_on`) and her username. null means the dashboard has not answered, or an older server
// does not say; the credits are never fetched until it is true. The username is for the checkout's return path:
// on a custom domain the address has no username in it.
let account = { on: null, unlimited: false, username: '' };
let accountVersion = 0;
const accountListeners = new Set();
export const captureCreditsOwner = () => accountVersion;
export const isCreditsOwner = owner => owner === accountVersion;

if (typeof window !== 'undefined') {
   window.addEventListener(CREDITS_EVENT, (event) => {
      if (event && event.detail && typeof event.detail === 'object') {
         latest = event.detail;
         updatedAt = Date.now();
      }
   });
}

// Credits are on for this creator and she pays for them (an AI Team creator is
// unlimited and never sees a balance).
export function isCreditsOn(payload) {
   return Boolean(payload && payload.enabled && !payload.unlimited);
}

// The monthly pack a payload names, or null. A payload carries it whenever her account holds one, even when
// credits are off for her or she is on the AI Team (then it is all Billing shows, so she can still cancel it).
export function creditsPack(payload) {
   return (payload && payload.monthly && payload.monthly.pack) || null;
}

// Call after any response that carries a `credits` payload: every chip, card
// and sheet on the page updates from the event.
export function publishCredits(payload, owner = accountVersion) {
   if (!isCreditsOwner(owner)) return;
   if (!payload || typeof payload !== 'object') return;
   latest = payload;
   updatedAt = Date.now();
   try {
      window.dispatchEvent(new CustomEvent(CREDITS_EVENT, { detail: payload }));
   } catch (e) {
      // No CustomEvent: the next read picks the payload up from `latest`.
   }
}

export function getLatestCredits() {
   return latest;
}

// Called once, when the dashboard data arrives. `on` is the server's agent_credits_on (anything but true counts as
// off); every useCredits() on the page follows it.
export function setCreditsAccount({ on, unlimited = false, username } = {}) {
   const next = { on: on === true, unlimited: unlimited === true, username: typeof username === 'string' ? username : '' };
   if (next.on === account.on && next.unlimited === account.unlimited && next.username === account.username) return;
   accountVersion += 1;
   latest = null; updatedAt = 0; inflight = null;
   account = next;
   accountListeners.forEach((listener) => listener());
}

// The dashboard data (the response that carries ai_team_active) in the shape setCreditsAccount takes. Only a true
// agent_credits_on counts; a missing one (an older server) is off.
export function creditsAccountFromDashboard(data) {
   const d = data || {};
   return {
      on: d.agent_credits_on === true,
      unlimited: d.ai_team_active === true,
      username: (d.influencer_info && d.influencer_info.influencer) || d.current_user || '',
   };
}

export function getCreditsAccount() { return account; }

export function getCreditsUsername() {
   return account.username;
}

// Tests only: forget everything the page has seen.
export function resetCreditsStore() {
   accountVersion += 1;
   latest = null;
   updatedAt = 0;
   inflight = null;
   account = { on: null, unlimited: false, username: '' };
}

function load() {
   if (inflight) return inflight;
   const requestedAccount = account;
   const owner = captureCreditsOwner();
   const pending = getCredits()
      .then((payload) => {
         if (account !== requestedAccount || !isCreditsOwner(owner)) return null;
         publishCredits(payload, owner);
         return latest;
      })
      .catch(() => {
         // Keep account eligibility independent from a missing balance.
         return isCreditsOwner(owner) ? latest : null;
      })
      .then((result) => {
         if (inflight === pending) inflight = null;
         return result;
      });
   inflight = pending;
   return pending;
}

/**
 * The credits payload for this page. Fetches it once (unless a response on
 * this page already carried it a moment ago) and follows every later
 * `withme:credits` event. It only fetches when the dashboard said credits are on
 * for her (setCreditsAccount): off or unknown, it just listens, so a creator
 * without credits never makes the request. `fetch: false` only listens too.
 */
export default function useCredits({ fetch = true } = {}) {
   const [credits, setCredits] = useState(latest);
   const [accountState, setAccountState] = useState(account);
   const on = accountState.on === true && !accountState.unlimited;

   useEffect(() => {
      const onAccount = () => { setAccountState(account); setCredits(latest); };
      accountListeners.add(onAccount);
      // The dashboard may have answered between the first render and now.
      onAccount();
      return () => { accountListeners.delete(onAccount); };
   }, []);

   useEffect(() => {
      const onCredits = (event) => {
         if (event && event.detail) setCredits(event.detail);
      };
      window.addEventListener(CREDITS_EVENT, onCredits);
      // Something may have been published between the first render and now.
      setCredits((current) => (latest && latest !== current ? latest : current));
      return () => window.removeEventListener(CREDITS_EVENT, onCredits);
   }, []);

   useEffect(() => {
      if (!fetch || !on || inflight) return;
      if (latest && Date.now() - updatedAt < FRESH_MS) return;
      load();
   }, [fetch, on, accountState]);

   const refresh = useCallback(() => (account.on === true && !account.unlimited ? load() : Promise.resolve(latest)), []);

   return { credits, owner: accountVersion, creditsOn: accountState.on === null ? isCreditsOn(credits) : accountState.on === true && !accountState.unlimited, account: accountState, loaded: credits !== null, refresh, setCredits: publishCredits };
}
